import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Pencil, QrCode } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QrCodeDialog } from '@/components/ui/qr-code-dialog';
import { Textarea } from '@/components/ui/textarea';
import { env } from '@/config/env';
import { useUploadEventImage } from '@/features/events/events.api';
import { cn } from '@/lib/utils';
import { Field, SetupEditor, SetupPage } from './setup-shell';
import {
  useCreateLocation,
  useDeleteLocation,
  useLocations,
  useUpdateLocation,
  type BookingLocationRecord,
  type LocationInput,
} from './setup.api';

/**
 * Locations — where a booking happens, and the QR code people check in with.
 *
 * A location carries three names: an internal one staff type, and an English
 * and a Spanish one customers see. All three are shown here, because this is
 * where they are maintained and a list that hides two of them is how a missing
 * translation goes unnoticed.
 */

const EMPTY: LocationInput = {
  name: '',
  nameEn: '',
  nameEs: '',
  description: '',
  descriptionEs: '',
  imageUrl: '',
};

function checkinUrl(location: BookingLocationRecord | null) {
  return location
    ? `${env.bookingWebfrontUrl}/checkin/location/${location.id}`
    : '';
}

export function LocationsPage() {
  const { t } = useTranslation();

  const list = useLocations();
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const remove = useDeleteLocation();
  const upload = useUploadEventImage();
  const fileInput = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState<BookingLocationRecord | null>(null);
  const [draft, setDraft] = useState<LocationInput>(EMPTY);
  const [qrFor, setQrFor] = useState<BookingLocationRecord | null>(null);

  const rows = list.data ?? [];
  const isEdit = !!editing?.id;
  const saving = create.isPending || update.isPending;

  const set = (patch: Partial<LocationInput>) =>
    setDraft((current) => ({ ...current, ...patch }));

  function open(row: BookingLocationRecord | null) {
    setEditing(row ?? ({ id: '' } as BookingLocationRecord));
    setDraft(
      row
        ? {
            name: row.name ?? '',
            nameEn: row.nameEn ?? '',
            nameEs: row.nameEs ?? '',
            description: row.description ?? '',
            descriptionEs: row.descriptionEs ?? '',
            imageUrl: row.imageUrl ?? '',
          }
        : EMPTY
    );
  }

  const failed = () => toast.error(t('bookingSetup.actionFailed'));
  const saved = () => {
    toast.success(t('bookingSetup.saved'));
    setEditing(null);
  };

  function save() {
    if (isEdit) {
      update.mutate(
        { id: editing!.id, ...draft },
        { onSuccess: saved, onError: failed }
      );
    } else {
      create.mutate(draft, { onSuccess: saved, onError: failed });
    }
  }

  return (
    <SetupPage
      title={t('bookingSetup.locationsTitle')}
      description={t('bookingSetup.locationsSubtitle')}
      onCreate={() => open(null)}
      onReload={() => void list.refetch()}
      reloading={list.isFetching}
      isPending={list.isPending}
      isError={list.isError}
      errorMessage={t('bookingSetup.loadFailed')}
      isEmpty={list.isSuccess && rows.length === 0}
      emptyMessage={t('bookingSetup.noLocations')}
    >
      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {row.imageUrl ? (
                    <img
                      src={row.imageUrl}
                      alt=""
                      className="bg-muted size-12 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="bg-muted size-12 shrink-0 rounded-lg" />
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {row.nameEn || row.name || '—'}
                    </p>
                    {row.nameEs && (
                      <p className="text-muted-foreground truncate text-xs">
                        {row.nameEs}
                      </p>
                    )}
                    {row.name && row.nameEn && (
                      <p className="text-muted-foreground truncate text-xs italic">
                        {row.name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQrFor(row)}
                  >
                    <QrCode className="size-3.5" />
                    {t('bookingSetup.qrCode')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => open(row)}>
                    <Pencil className="size-3.5" />
                    {t('bookingSetup.edit')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SetupEditor
        title={
          isEdit ? t('bookingSetup.editLocation') : t('bookingSetup.newLocation')
        }
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSave={save}
        saving={saving}
        canSave={!!draft.name?.trim() || !!draft.nameEn?.trim()}
        isEdit={isEdit}
        deleting={remove.isPending}
        deletePrompt={t('bookingSetup.deleteLocationPrompt')}
        onDelete={() =>
          remove.mutate(editing!.id, {
            onSuccess: () => {
              toast.success(t('bookingSetup.deleted'));
              setEditing(null);
            },
            onError: failed,
          })
        }
      >
        <Field
          label={t('bookingSetup.internalName')}
          hint={t('bookingSetup.internalNameHint')}
        >
          <Input
            value={draft.name ?? ''}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={`${t('bookingSetup.name')} (EN)`}>
            <Input
              value={draft.nameEn ?? ''}
              onChange={(e) => set({ nameEn: e.target.value })}
            />
          </Field>
          <Field label={`${t('bookingSetup.name')} (ES)`}>
            <Input
              value={draft.nameEs ?? ''}
              onChange={(e) => set({ nameEs: e.target.value })}
            />
          </Field>
        </div>

        <Field label={`${t('bookingSetup.description')} (EN)`}>
          <Textarea
            rows={2}
            value={draft.description ?? ''}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>
        <Field label={`${t('bookingSetup.description')} (ES)`}>
          <Textarea
            rows={2}
            value={draft.descriptionEs ?? ''}
            onChange={(e) => set({ descriptionEs: e.target.value })}
          />
        </Field>

        <Field label={t('bookingSetup.image')}>
          <div className="space-y-2">
            {draft.imageUrl && (
              <img
                src={draft.imageUrl}
                alt=""
                className="bg-muted max-h-40 w-full rounded-lg object-contain"
              />
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(fileEvent) => {
                const file = fileEvent.target.files?.[0];
                if (!file) return;
                upload.mutate(file, {
                  onSuccess: (url) => set({ imageUrl: url }),
                  onError: () => toast.error(t('bookingSetup.uploadFailed')),
                });
                fileEvent.target.value = '';
              }}
            />
            <Button
              variant="outline"
              className="w-full border-dashed"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <ImagePlus />
              )}
              {t('bookingSetup.uploadImage')}
            </Button>
            {/* The URL stays editable: the original took one by hand, and a
                location image is sometimes already hosted elsewhere. */}
            <Input
              value={draft.imageUrl ?? ''}
              placeholder={t('bookingSetup.imageUrl')}
              onChange={(e) => set({ imageUrl: e.target.value })}
            />
          </div>
        </Field>
      </SetupEditor>

      <QrCodeDialog
        title={qrFor?.nameEn || qrFor?.name || t('bookingSetup.qrCode')}
        caption={qrFor?.nameEn || qrFor?.name || ''}
        url={checkinUrl(qrFor)}
        fileBase={`location-${qrFor?.id ?? ''}-qr`}
        open={qrFor !== null}
        onOpenChange={(next) => {
          if (!next) setQrFor(null);
        }}
      />
    </SetupPage>
  );
}
